'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, Menu, X } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

type NavLink =
  | { label: string; href: string; pending?: false }
  | { label: string; pending: true };

const navLinks: NavLink[] = [
  { label: 'Browse', href: '/browse' },
  { label: 'How it works', href: '/how-it-works' },
  { label: 'About', href: '/about-us' },
  { label: 'Trust & Safety', href: '/trust-safety-community' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
  // /** Placeholder entries — swap for real `href` when pages ship */
  // { label: 'Blog', pending: true },
  // { label: 'Press', pending: true },
];

export const Header: React.FC = () => {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Auth state drives the right-side CTA cluster (Login/Sign Up vs Go to Dashboard).
   *  Middleware role-routes /dashboard → /admin/dashboard for admins, so a single target
   *  is sufficient here regardless of role. */
  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 h-[72px] sm:h-[88px] lg:h-[108px] transition-shadow duration-200 bg-v2-neutral-0
        ${scrolled ? 'shadow-sm border-b border-v2-neutral-200' : 'border-b border-v2-neutral-200'}`}
    >
      {/* Brand accent strip — sits flush above the nav row on every page. */}
      <div aria-hidden="true" className="h-1 w-full bg-v2-brand" />
      <div className="max-w-v2 mx-auto px-4 sm:px-6 lg:px-8 h-[68px] sm:h-[84px] lg:h-[104px] flex items-center gap-0">
        {/* Logo — scaled down on mobile so the header stays compact. */}
        <Link
          href="/"
          aria-label="Rent It Forward — back to home"
          className="relative mr-4 sm:mr-10 md:mr-12 flex shrink-0 items-center py-1"
        >
          <Image
            src="/images/Rent-IT-Forward-Logo.png"
            alt="Rent It Forward"
            width={440}
            height={120}
            className="h-10 w-auto max-h-10 sm:h-14 sm:max-h-14 md:h-16 md:max-h-16 lg:h-20 lg:max-h-20"
            priority
          />
        </Link>

        {/* Nav links — desktop */}
        <nav className="hidden md:flex flex-1 flex-wrap items-center gap-0.5 lg:gap-1" aria-label="Primary">
          {navLinks.map((link) => {
            if ('pending' in link && link.pending) {
              return (
                <span
                  key={link.label}
                  className="inline-flex cursor-not-allowed items-center gap-1.5 whitespace-nowrap rounded-v2-xl px-3 py-2 text-[14px] font-medium text-v2-neutral-400"
                  title="Coming soon"
                >
                  {link.label}
                  <span className="rounded-v2-full bg-v2-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-v2-neutral-500">
                    Soon
                  </span>
                </span>
              );
            }
            const active = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`whitespace-nowrap px-3.5 py-2 text-[14px] font-semibold transition-colors duration-150
                  ${active
                    ? 'rounded-none border-b-2 border-v2-brand pb-[6px] text-v2-brand'
                    : 'text-v2-neutral-600 hover:rounded-v2-xl hover:bg-v2-neutral-50 hover:text-v2-neutral-900'
                  }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Auth CTA cluster — desktop.
            • While auth bootstraps, render an invisible placeholder of the same width as
              the two-button cluster so the layout doesn't shift when the session resolves.
            • Authenticated:   single "Go to Dashboard" pill.
            • Unauthenticated: Log In + Sign Up pair (unchanged from before). */}
        <div className="hidden md:flex items-center gap-2 ml-auto shrink-0">
          {authLoading ? (
            <div aria-hidden className="h-[42px] w-[200px]" />
          ) : isAuthenticated ? (
            <Link
              href="/dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-v2-full leading-[18px] bg-v2-green-500 text-white text-[14px] font-semibold hover:bg-v2-green-600 transition-colors"
            >
              <LayoutDashboard size={16} strokeWidth={2.25} aria-hidden />
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link
                href="/login"
                className="px-5 py-2.5 rounded-v2-full leading-[18px] border-[1.5px] border-v2-neutral-300 text-[14px] font-semibold text-v2-neutral-700 hover:bg-v2-neutral-50 transition-colors"
              >
                Log In
              </Link>
              <Link
                href="/signup"
                className="px-5 py-2.5 rounded-v2-full leading-[18px] bg-v2-green-500 text-white text-[14px] font-semibold hover:bg-v2-green-600 transition-colors"
              >
                Sign Up
              </Link>
            </>
          )}
        </div>

        {/* Hamburger — mobile */}
        <button
          className="md:hidden ml-auto p-2 rounded-v2-md text-v2-neutral-700 hover:bg-v2-neutral-100"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-v2-neutral-0 border-t border-v2-neutral-100 px-4 py-4 flex flex-col gap-2 shadow-lg">
          {navLinks.map((link) => {
            if ('pending' in link && link.pending) {
              return (
                <span
                  key={link.label}
                  className="flex items-center justify-between gap-2 px-3 py-2.5 text-[15px] font-medium text-v2-neutral-400"
                >
                  {link.label}
                  <span className="shrink-0 rounded-v2-full bg-v2-neutral-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-v2-neutral-500">
                    Soon
                  </span>
                </span>
              );
            }
            const active = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className={`px-3 py-2.5 text-[15px] font-medium rounded-v2-md hover:bg-v2-neutral-50 ${
                  active ? 'text-v2-brand font-semibold' : 'text-v2-neutral-700'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <div className="flex gap-2 pt-2 border-t border-v2-neutral-100 mt-2">
            {authLoading ? null : isAuthenticated ? (
              <Link
                href="/dashboard"
                onClick={() => setMenuOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 text-center px-4 py-2.5 bg-v2-brand hover:bg-v2-brand-hover rounded-v2-full text-[14px] font-semibold text-white"
              >
                <LayoutDashboard size={16} strokeWidth={2.25} aria-hidden />
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 text-center px-4 py-2.5 border border-v2-neutral-300 rounded-v2-full text-[14px] font-semibold text-v2-neutral-700"
                >
                  Log In
                </Link>
                <Link
                  href="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="flex-1 text-center px-4 py-2.5 bg-v2-green-600  hover:bg-v2-green-600 rounded-v2-full text-[14px] font-semibold text-white"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

